import './SetupAuditTrailTable.scss';
import { AuditTrailExportButton } from './AuditTrailExportButton';
import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { createAsyncSelectors } from '@lib/selectors';
import { getColumns } from './SetupAuditTrailTableColumns';
import { queryParamsToDynamicQueryLanguageParams } from '@sonar-software/sonar-ts-lib';
import { ActionFilter, ClassificationFilter, SectionFilter, SourceNamePrefixFilter } from './filters';
import { coreActions, SchemaFormat } from '@sonar-software/sonar-core';
import { DynamicQueryOperator, SonarV3SortDirection } from '@sonar-software/sonar-ts-types';
import { isEqual, uniqueId } from 'lodash';
import { PaginatedTable } from '@ui/tables/PaginatedTable';
import React, { useEffect, useRef } from 'react';
import { TableModifier } from '@ui/table-modifier';
import { useAction, useAsyncState, useQueryParams } from '@hooks';
const getRowKey = (result) => `${result.auditDate}-${uniqueId()}`;
const columns = getColumns();
const filters = [{
        filterComponent: ActionFilter,
        filterFormat: SchemaFormat.Text,
        filterId: 'Action',
        filterLabel: COPY_SPECIFIC.ACTION
    }, {
        filterFormat: SchemaFormat.SalesforceUser,
        filterId: 'User',
        filterLabel: COPY_SPECIFIC.USER
    }, {
        filterComponent: SectionFilter,
        filterFormat: SchemaFormat.Object,
        filterId: 'Section',
        filterLabel: COPY_SPECIFIC.SECTION
    }, {
        filterFormat: SchemaFormat.SalesforceUser,
        filterId: 'DelegateUser',
        filterLabel: COPY_SPECIFIC.DELEGATE_USER
    }, {
        filterComponent: SourceNamePrefixFilter,
        filterFormat: SchemaFormat.Object,
        filterId: 'SourceNamePrefix',
        filterLabel: COPY_SPECIFIC.SOURCE_NAMESPACE_PREFIX
    }, {
        filterComponent: ClassificationFilter,
        filterFormat: SchemaFormat.Object,
        filterId: 'Classification',
        filterLabel: COPY_SPECIFIC.CLASSIFICATION
    }];
const sortOptions = [
    {
        direction: SonarV3SortDirection.Descending,
        key: 'AuditDate',
        label: COPY_SPECIFIC.DATE
    },
    {
        direction: SonarV3SortDirection.Ascending,
        key: 'Action',
        label: COPY_SPECIFIC.ACTION
    },
    {
        direction: SonarV3SortDirection.Ascending,
        key: 'SourceNamePrefix',
        label: COPY_SPECIFIC.SOURCE_NAMESPACE_PREFIX
    },
    {
        direction: SonarV3SortDirection.Ascending,
        key: 'Section',
        label: COPY_SPECIFIC.SECTION
    }
];
const setupAuditTrailSelectors = createAsyncSelectors((state) => state.core.dataSecurity.setupAuditTrail.get);
const convertToDql = (params) => queryParamsToDynamicQueryLanguageParams(params, {
    filterParams: {
        Action: {
            operator: DynamicQueryOperator.Like,
            value: (filter) => filter === null || filter === void 0 ? void 0 : filter.value
        },
        Classification: {
            value: (filter) => filter === null || filter === void 0 ? void 0 : filter.value
        },
        DelegateUser: {
            value: (filter) => filter === null || filter === void 0 ? void 0 : filter.value
        },
        Section: {
            operator: DynamicQueryOperator.In,
            value: (filter) => filter === null || filter === void 0 ? void 0 : filter.value
        },
        SourceNamePrefix: {
            operator: DynamicQueryOperator.In,
            value: (filter) => filter === null || filter === void 0 ? void 0 : filter.value
        },
        User: {
            value: (filter) => filter === null || filter === void 0 ? void 0 : filter.value
        },
        start: {
            name: 'AuditDate',
            rangeEnd: 'end'
        }
    }
});
export const SetupAuditTrailTable = ({ classificationsFilter, dateRange, onClassificationFilterChange }) => {
    var _a;
    const getSetupAuditTrailEvents = useAction(coreActions.requestGetCoreSetupAuditTrail);
    const { data, isRequested } = useAsyncState(setupAuditTrailSelectors);
    const updatingRef = useRef(false);
    const { queryParams, updateParams } = useQueryParams({
        defaultParams: {
            sortBy: sortOptions[0].key,
            sortDirection: sortOptions[0].direction
        },
        onUpdate: (newParams, prev, combined) => {
            var _a, _b;
            const { key, paginationDirection } = newParams;
            const classifcations = (_b = (_a = combined.Classification) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : [];
            if (!isEqual(classifcations, classificationsFilter)) {
                onClassificationFilterChange(classifcations);
                updatingRef.current = true;
            }
            const dql = convertToDql(Object.assign(Object.assign({}, combined), { key,
                paginationDirection }));
            getSetupAuditTrailEvents(dql);
        },
        transform: (newParams, prev, combined) => {
            // Recalculated filter string when external filters change.
            combined.filters = convertToDql(combined).filters;
            return combined;
        }
    });
    useEffect(() => {
        if (dateRange.end !== (queryParams === null || queryParams === void 0 ? void 0 : queryParams.end) || dateRange.start !== (queryParams === null || queryParams === void 0 ? void 0 : queryParams.start)) {
            const { start, end } = dateRange;
            updateParams({
                end,
                start
            });
        }
    }, [dateRange, queryParams, queryParams === null || queryParams === void 0 ? void 0 : queryParams.end, queryParams === null || queryParams === void 0 ? void 0 : queryParams.start, updateParams]);
    useEffect(() => {
        var _a;
        if (!updatingRef.current && !isEqual(classificationsFilter, (_a = queryParams.Classification) === null || _a === void 0 ? void 0 : _a.value)) {
            updateParams({
                Classification: {
                    format: SchemaFormat.Object,
                    value: classificationsFilter
                }
            });
        }
        updatingRef.current = false;
    }, [classificationsFilter, (_a = queryParams.Classification) === null || _a === void 0 ? void 0 : _a.value, updateParams]);
    return (React.createElement("div", { id: 'setup-audit-trail-table' },
        React.createElement(TableModifier, { convertToDynamicQueryLanguage: (params) => {
                updateParams(params);
                return convertToDql(params);
            }, extra: React.createElement(AuditTrailExportButton, { queryParams: convertToDql(queryParams) }), filterDisplayType: 'menu', filters: filters, filtersString: queryParams.filters, onChange: updateParams, orderString: queryParams.ordering, sortOptions: sortOptions }),
        React.createElement(PaginatedTable, { columns: columns, defaultPageSize: queryParams.count, loading: isRequested, onChange: updateParams, resource: data, rowKey: getRowKey, scroll: {
                scrollToFirstRowOnChange: true,
                x: 'max-content'
            }, sortBy: queryParams.sortBy, sortDirection: queryParams.sortDirection, sticky: true })));
};
